import {useEffect, useState} from 'react'
import {captureMessage, withScope} from '@sentry/react'
import {twJoin} from 'tailwind-merge'
import {useIntersectionObserver} from 'usehooks-ts'

import {ImageSkeletonPlaceholder} from './ImageSkeletonPlaceholder'

import type {ComponentProps, ReactEventHandler} from 'react'

export interface LazyImageProps
  extends Omit<ComponentProps<'img'>, 'ref' | 'alt' | 'src'> {
  src?: string | null
  alt?: string
  placeholderSrc?: string | null
  logError?: boolean
  logPlaceholderError?: boolean
  hasBackground?: boolean
}

export enum ImageType {
  Placeholder = 'placeholder',
  Highres = 'highres',
}

const captureLoadErrorException = (
  image: HTMLImageElement,
  imageType: ImageType,
  src: ComponentProps<'img'>['src'] | null,
) => {
  const imageContext = {
    imageType,
    alt: image.alt,
    imageElementSrc: image.src,
    imageComponentSrc: src,
  }

  withScope(scope => {
    scope.setTag('image.alt', image.alt)
    scope.setTag('image.type', imageType)
    captureMessage(
      'Could not load image.',
      {
        level: 'error',
        contexts: {image: imageContext},
      },
    )
  })
}

export const LazyImage = (props: LazyImageProps) => {
  const {
    alt = '',
    className = '',
    hasBackground = true,
    loading = 'lazy',
    logError = true,
    logPlaceholderError = false, // we don't use it everywhere because it will often throw an error
    onError,
    placeholderSrc: _placeholderSrc,
    src: _src,
    ...restProps
  } = props

  const placeholderSrc = _placeholderSrc || ''
  const src = _src || ''

  const {ref, isIntersecting} = useIntersectionObserver({freezeOnceVisible: true, rootMargin: '300px'})
  const [currentSrc, setCurrentSrc] = useState<string>(placeholderSrc || src)
  const [hasSrcError, setHasSrcError] = useState(false)
  const [hasPlaceholderError, setHasPlaceholderError] = useState(false)

  useEffect(() => {
    if (!isIntersecting || currentSrc === src) return

    const img = new Image()
    img.onload = () => setCurrentSrc(src)
    img.onerror = () => setHasSrcError(true)
    img.src = src
  }, [src, currentSrc, isIntersecting])

  const handleError: ReactEventHandler<HTMLImageElement> = (event) => {
    if (currentSrc === placeholderSrc) {
      setHasPlaceholderError(true)
      if (logError) captureLoadErrorException(event.currentTarget, ImageType.Placeholder, placeholderSrc)
    }
    if (currentSrc === src) {
      setHasSrcError(true)
      if (logPlaceholderError) captureLoadErrorException(event.currentTarget, ImageType.Highres, src)
    }

    onError?.(event)
  }

  // The skeleton is a backup for when src and placeholder both don't exist or can't load
  const showSkeletonPlaceholder = (!src || hasSrcError) && (!placeholderSrc || hasPlaceholderError)
  if (showSkeletonPlaceholder) return <ImageSkeletonPlaceholder {...props} />

  return (
    <img
      alt={alt}
      className={twJoin(
        className,
        hasBackground && 'bg-media-bg',
      )}
      loading={loading}
      onError={handleError}
      ref={ref}
      src={currentSrc}
      {...restProps}
    />
  )
}
